import React, { useState } from 'react';
import * as yup from 'yup';
import { useFormik, FormikProvider, FieldArray } from 'formik';
import { useSnackbar } from 'notistack';
import { useDispatch } from 'react-redux';
import { Box, DialogContent, DialogActions, Button } from '@mui/material';
import BootstrapDialog, { BootstrapDialogTitle } from '../../components/common/dialog/BootstrapDialog';
import DetailTemplate from '../../components/common/template/DetailTemplate';
import AskDialog from '../../components/common/dialog/AskDialog';
import EsgForm from '../../components/esgmng/EsgForm';
import EditorViewComponent from '../../components/common/editor/EditorViewComponent';
import EsgFileMultiForm from '../../components/esgmng/EsgFileMultiForm';
import EsgFileListContainer from './EsgFileListContainer';
import * as esgApi from '../../lib/api/esg';
import * as fileApi from '../../lib/api/file';
import { listEsgBoards } from '../../modules/esgModule/esgBoards';

const EsgFormContainer = ({ type, data, open, handleClose, searchData }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = {
    wrTitle: data?.wrTitle || '',
    wrCategory: data?.wrCategory || '',
    wrContent: data?.wrContent || '',
    files: [{ fileDesc: '', file: null }],
  };

  const validationSchema = yup.object({
    wrTitle: yup.string().required('제목을 입력하세요'),
    wrContent: yup.string().required('내용을 입력하세요'),
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      handleClose();
      setSubmitting(false);
    },
  });

  const onFileSave = async (value, board) => {
    const formData = new FormData();
    formData.append('fileDt', value.file);
    formData.append('fileTyp', 'esg');
    formData.append('fileAttrVal1', board.wrUuid);
    formData.append('fileDesc', value.fileDesc);
    try {
      await fileApi.addFiles(formData);
      enqueueSnackbar('첨부파일이 등록되었습니다.', { variant: 'success' });
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    dispatch(listEsgBoards(searchData));
  };

  const onSave = async (values) => {
    const dataToSubmit = {
      wrTitle: values.wrTitle,
      wrCategory: values.wrCategory,
      wrContent: values.wrContent,
      useYn: 'Y',
    };

    const hasFiles = values.files.length > 0 && values.files[0].file !== null;

    if (type === 'add') {
      try {
        const response = await esgApi.addEsgBoard(dataToSubmit);
        if (response.data) {
          if (hasFiles) {
            values.files.forEach((value) => onFileSave(value, response.data));
          } else {
            dispatch(listEsgBoards(searchData));
          }
          enqueueSnackbar('ESG 게시물이 등록되었습니다.', { variant: 'success' });
        }
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }
    } else {
      try {
        const response = await esgApi.editEsgBoard(data.wrUuid, dataToSubmit);
        if (response.data.info) {
          if (hasFiles) {
            values.files.forEach((value) => onFileSave(value, response.data.info));
          } else {
            dispatch(listEsgBoards(searchData));
          }
          enqueueSnackbar('ESG 게시물이 수정되었습니다.', { variant: 'success' });
        }
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }
    }
  };

  const [visible, setVisible] = useState(false);
  const onDelete = () => setVisible(true);
  const onCancle = () => setVisible(false);
  const onConfirm = () => { setVisible(false); onRemove(); };

  const onRemove = async () => {
    try {
      const res = await esgApi.removeEsgBoard(data.wrUuid);
      if (res.data.result === 'success') {
        dispatch(listEsgBoards());
        handleClose();
        enqueueSnackbar('ESG 게시물이 삭제되었습니다.', { variant: 'success' });
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <BootstrapDialog onClose={handleClose} open={open} fullWidth maxWidth="md">
      <BootstrapDialogTitle id="esg-dialog-title" onClose={handleClose}>
        ESG {type === 'edit' ? '수정' : '추가'}
      </BootstrapDialogTitle>
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <DialogContent dividers sx={{ height: 620, overflowY: 'auto' }}>
          <DetailTemplate title="ESG 정보">
            <EsgForm formik={formik} />
          </DetailTemplate>

          <DetailTemplate title="첨부 파일">
            <FormikProvider value={formik}>
              <FieldArray
                name="files"
                render={(arrayHelpers) => (
                  <Box>
                    {formik.values.files.map((f, index) => (
                      <EsgFileMultiForm
                        key={index}
                        file={f}
                        index={index}
                        touched={formik.touched}
                        errors={formik.errors}
                        handleChange={formik.handleChange}
                        handleBlur={formik.handleBlur}
                        handleRemove={() => arrayHelpers.remove(index)}
                      />
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 2 }}>
                      <Button
                        color="secondary"
                        variant="outlined"
                        onClick={() => arrayHelpers.push({ fileDesc: '', file: null })}
                      >
                        파일 추가
                      </Button>
                    </Box>
                  </Box>
                )}
              />
            </FormikProvider>
          </DetailTemplate>

          {type === 'edit' && (
            <DetailTemplate title="첨부 서류 리스트">
              <EsgFileListContainer board={data} />
            </DetailTemplate>
          )}
        </DialogContent>

        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            {type === 'edit' && (
              <Button type="button" variant="contained" color="error" onClick={onDelete}>
                삭제
              </Button>
            )}
          </Box>
          <Box>
            <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
              저장
            </Button>
          </Box>
        </DialogActions>
      </Box>

      <AskDialog
        visible={visible}
        title="ESG 게시물 삭제"
        description="선택한 게시물을 삭제하시겠습니까? (복구 불가)"
        confirmText="삭제"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </BootstrapDialog>
  );
};

export default EsgFormContainer;
